import { useMemo, useState } from 'react'
import { AlertTriangle, CalendarDays, ChevronDown, Download, FlaskConical, Ruler, TestTube } from 'lucide-react'
import type { BuildRange, ClinSparkStudyPackage } from '../../types'
import { anchorId, displayLabel, downloadText, known, pluralize, rows, toCsv, value, type Row } from '../../utils/parser'
import { Badge, Highlight, Metric, Notice, SearchInput, Section, SegmentedControl } from './StudyUi'
import type { NavigateFn } from './BuildBlueprint'
import { packageRanges } from './RangesTab'

type Analyte = { name: string; sourceLabel: string | null; note: string | null; verify: boolean }
type Panel = {
  id: string
  name: string
  family: string | null
  specimen: string | null
  laboratory: string | null
  comment: string | null
  analytes: Analyte[]
  visits: string[]
  procedures: string[]
  scheduled: boolean
  pages: string[]
  ready: boolean
  reason: string | null
  ranges: BuildRange[]
}

type StatusFilter = 'all' | 'ready' | 'review' | 'unscheduled'

function normalizePanels(data: ClinSparkStudyPackage): Panel[] {
  const cmm = (data.study_build_design?.CMM || {}) as Row
  const visitNames = new Map(rows(cmm.visit_dictionary).map((visit) => [String(visit.visit_id), displayLabel(visit.visit_name)]))
  const ranges = packageRanges(data)
  const dictionary = rows(cmm.laboratory_dictionary)
  const source = dictionary.length ? dictionary : rows(data.lab_panels)
  return source.map((panel, index) => {
    const details = rows(panel.analyte_details)
    const analytes: Analyte[] = details.length
      ? details.map((item) => ({
        name: String(item.test_name),
        sourceLabel: known(item.source_label) && item.source_label !== item.test_name ? String(item.source_label) : null,
        note: known(item.note) ? String(item.note) : null,
        verify: item.catalog_match === false,
      }))
      : (Array.isArray(panel.analytes) ? panel.analytes : rows(panel.tests).map((test) => test.test_name))
        .filter(known).map((name: unknown) => ({ name: String(name), sourceLabel: null, note: null, verify: false }))
    const name = String(panel.panel_name || `Panel ${index + 1}`)
    const visitIds = (Array.isArray(panel.visit_schedule) ? panel.visit_schedule : []).map(String)
    const scheduled = panel.scheduled_in_soa !== false && (visitIds.length > 0 || panel.scheduled_in_soa === true)
    return {
      id: String(panel.panel_id || anchorId('lab', name, index)),
      name,
      family: known(panel.panel_family) ? String(panel.panel_family) : null,
      specimen: known(panel.specimen_type) ? String(panel.specimen_type) : null,
      laboratory: known(panel.laboratory) ? String(panel.laboratory) : null,
      comment: known(panel.comment) ? String(panel.comment) : null,
      analytes,
      visits: visitIds.map((id) => visitNames.get(id) || id),
      procedures: (Array.isArray(panel.procedure_names) ? panel.procedure_names : []).map(String),
      scheduled,
      pages: (Array.isArray(panel.source_pages) ? panel.source_pages : []).map(String),
      ready: analytes.length > 0 && !panel.human_review_required,
      reason: known(panel.reason) ? String(panel.reason) : null,
      ranges: ranges.filter((range) => (range.lab_panel_names || []).includes(name)),
    }
  })
}

const SPECIMEN_ICON = (specimen: string | null) => (specimen && /urine/i.test(specimen) ? TestTube : FlaskConical)

export default function LabPanelsTab({ data, search, onSearchChange, onNavigate }: {
  data: ClinSparkStudyPackage
  search: string
  onSearchChange: (next: string) => void
  onNavigate?: NavigateFn
}) {
  const panels = useMemo(() => normalizePanels(data), [data])
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(panels.length > 8 ? panels.map((panel) => panel.id) : []))

  const query = search.trim().toLowerCase()
  const matches = (panel: Panel) => !query
    || `${panel.name} ${panel.specimen || ''} ${panel.laboratory || ''} ${panel.comment || ''} ${panel.procedures.join(' ')}`.toLowerCase().includes(query)
    || panel.analytes.some((analyte) => `${analyte.name} ${analyte.sourceLabel || ''} ${analyte.note || ''}`.toLowerCase().includes(query))
  const visible = panels.filter((panel) => matches(panel) && (
    statusFilter === 'all'
    || (statusFilter === 'ready' && panel.ready)
    || (statusFilter === 'review' && !panel.ready)
    || (statusFilter === 'unscheduled' && !panel.scheduled)))

  const analyteCount = panels.reduce((total, panel) => total + panel.analytes.length, 0)
  const needsReview = panels.filter((panel) => !panel.ready).length
  const unscheduled = panels.filter((panel) => !panel.scheduled).length

  const exportCsv = () => {
    const header = ['Panel', 'Specimen', 'Laboratory', 'Analyte', 'Protocol label', 'Analyte note', 'Scheduled visits', 'SoA procedures', 'Panel comment', 'PDF pages']
    const body = panels.flatMap((panel) => (panel.analytes.length ? panel.analytes : [null]).map((analyte) => [
      panel.name, panel.specimen || '', panel.laboratory || '', analyte?.name || '', analyte?.sourceLabel || '', analyte?.note || '',
      panel.visits.join('; '), panel.procedures.join('; '), panel.comment || '', panel.pages.join(', '),
    ]))
    downloadText('laboratory_panels.csv', toCsv(header, body), 'text/csv;charset=utf-8')
  }

  if (!panels.length) {
    return (
      <Section title="Laboratory Panels" description="Panels and analytes from the Schedule of Activities and the protocol's laboratory appendix.">
        <Notice>No laboratory panels were found. Panels come from Schedule of Activities rows (e.g. “Hematology”) and analytes from the laboratory appendix table.</Notice>
      </Section>
    )
  }

  const allCollapsed = visible.every((panel) => collapsed.has(panel.id))
  const toggle = (id: string) => setCollapsed((current) => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  return (
    <Section
      title="Laboratory Panels"
      description="Each panel combines the Schedule of Activities (when it is collected) with the laboratory appendix (what is measured, by which laboratory)."
      actions={<button type="button" className="btn-secondary" onClick={exportCsv}><Download className="mr-1.5 h-4 w-4" aria-hidden={true} />Export CSV</button>}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label="Panels" value={panels.length} />
        <Metric label="Analytes" value={analyteCount} />
        <Metric label="Not in the SoA" value={unscheduled} tone={unscheduled ? 'amber' : undefined} hint="Listed in the appendix, but no Schedule of Activities row names the panel" />
        <Metric label="Needs review" value={needsReview} tone={needsReview ? 'amber' : undefined} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput value={search} onChange={onSearchChange} placeholder="Search panels or analytes" className="w-full sm:w-72" />
        <SegmentedControl label="Panel status" options={[['all', 'All'], ['ready', 'Ready'], ['review', 'Needs review'], ['unscheduled', 'Not in SoA']] as const} valueKey={statusFilter} onChange={setStatusFilter} />
        <button type="button" className="ml-auto text-sm font-medium text-primary-600 hover:underline dark:text-primary-400" onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(visible.map((panel) => panel.id)))}>
          {allCollapsed ? 'Expand all' : 'Collapse all'}
        </button>
      </div>

      <div className="space-y-3">
        {visible.map((panel) => (
          <PanelCard key={panel.id} panel={panel} open={!collapsed.has(panel.id)} onToggle={() => toggle(panel.id)} search={search} onNavigate={onNavigate} />
        ))}
        {visible.length === 0 && <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">No panels match the current filters.</p>}
      </div>
    </Section>
  )
}

function PanelCard({ panel, open, onToggle, search, onNavigate }: {
  panel: Panel
  open: boolean
  onToggle: () => void
  search: string
  onNavigate?: NavigateFn
}) {
  const Icon = SPECIMEN_ICON(panel.specimen)
  const bodyId = `${anchorId('lab', panel.id)}-body`
  const query = search.trim().toLowerCase()
  return (
    <article id={anchorId('lab', panel.id)} className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={bodyId} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"><Icon className="h-4 w-4" aria-hidden={true} /></span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-gray-900 dark:text-white"><Highlight text={panel.name} query={search} /></span>
            {panel.specimen && <Badge>{panel.specimen}</Badge>}
            {panel.laboratory && <Badge tone="blue">{panel.laboratory}</Badge>}
          </span>
          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
            {pluralize(panel.analytes.length, 'analyte')} · {panel.visits.length ? `collected at ${pluralize(panel.visits.length, 'visit')}` : 'no scheduled visits'}
            {panel.pages.length > 0 && ` · appendix PDF p. ${panel.pages.join(', ')}`}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {!panel.scheduled && <Badge tone="amber" title="No Schedule of Activities row names this panel"><AlertTriangle className="h-3 w-3" aria-hidden={true} />Not in SoA</Badge>}
          <Badge tone={panel.ready ? 'green' : 'amber'}>{panel.ready ? 'Ready' : 'Needs review'}</Badge>
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden={true} />
        </span>
      </button>
      {open && (
        <div id={bodyId} className="grid gap-5 border-t border-gray-100 px-4 py-4 dark:border-gray-700 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="space-y-3">
            <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Analytes</h5>
            {panel.analytes.length ? (
              <ul className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2 xl:grid-cols-3">
                {panel.analytes.map((analyte) => {
                  const hit = query && `${analyte.name} ${analyte.sourceLabel || ''}`.toLowerCase().includes(query)
                  return (
                    <li key={analyte.name} className={`rounded px-1.5 py-1 text-sm ${hit ? 'bg-yellow-100/80 dark:bg-yellow-900/30' : ''}`}>
                      <span className="text-gray-900 dark:text-gray-100">{analyte.name}</span>
                      {analyte.verify && <span className="ml-1 text-2xs font-medium uppercase text-amber-600 dark:text-amber-400" title="Not in the analyte catalog; confirm the name">verify</span>}
                      {analyte.sourceLabel && <span className="block text-xs text-gray-500 dark:text-gray-400">“{analyte.sourceLabel}”</span>}
                      {analyte.note && <span className="block text-xs text-gray-500 dark:text-gray-400">{analyte.note}</span>}
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="text-sm text-amber-700 dark:text-amber-300">{panel.reason || 'The protocol does not list analytes for this panel.'}</p>
            )}
            {panel.comment && <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-900/60 dark:text-gray-200">{panel.comment}</p>}
          </div>
          <div className="space-y-4 text-sm">
            <div>
              <h5 className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"><CalendarDays className="h-3.5 w-3.5" aria-hidden={true} />Schedule</h5>
              {panel.visits.length ? (
                <div className="flex flex-wrap gap-1">{panel.visits.map((visit, index) => <Badge key={`${visit}-${index}`}>{visit}</Badge>)}</div>
              ) : <p className="text-gray-500 dark:text-gray-400">{panel.scheduled ? 'Scheduled in the SoA; visit mapping not resolved.' : 'Not scheduled in the Schedule of Activities.'}</p>}
              {panel.procedures.length > 0 && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">SoA rows: {panel.procedures.join('; ')}</p>}
            </div>
            {panel.ranges.length > 0 && (
              <div>
                <h5 className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"><Ruler className="h-3.5 w-3.5" aria-hidden={true} />Protocol ranges</h5>
                <ul className="space-y-1">
                  {panel.ranges.map((range) => {
                    const label = `${range.item_name}: ${(range.range_type === 'eligibility' ? range.acceptable : range.stated)?.display || value(null)}`
                    return (
                      <li key={range.range_id}>
                        {onNavigate
                          ? <button type="button" onClick={() => onNavigate('ranges', anchorId('range', range.range_id))} className="text-left text-sky-700 hover:underline dark:text-sky-300">{label}</button>
                          : <span>{label}</span>}
                        <span className="text-xs text-gray-500 dark:text-gray-400"> · {range.criterion_ref || (range.range_type === 'eligibility' ? 'eligibility' : 'monitoring')}</span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  )
}
